import {
  Box,
  Button,
  Center,
  Container,
  Flex,
  HStack,
  Hide,
  Icon,
  IconButton,
  Image,
  Show,
  Spinner,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { LucideArrowRight, LucideShieldAlert } from "lucide-react";
import { NextSeo } from "next-seo";
import Link from "next/link";
import { useRouter } from "next/router";
import React, { useEffect, useState } from "react";

import { useSession } from "@chainpatrol/auth/react";
import { DevToolbar } from "@chainpatrol/devtools";

import { NotFoundError } from "~/lib/error";
import { trpc } from "~/lib/trpc";

const ADMIN_DASHBOARD_PATTERN = /^\/admin\/([^/]+)\/preview\/?.*$/;

export function ShellForOrg({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const session = useSession();

  const [isAdminDashboardPreview, setIsAdminDashboardPreview] = useState(false);
  useEffect(() => {
    setIsAdminDashboardPreview(
      typeof window !== "undefined" &&
        typeof parent !== "undefined" &&
        parent !== window &&
        ADMIN_DASHBOARD_PATTERN.test(parent.window.location.pathname),
    );
  }, []);

  const getOrgBySlugPublicQuery = trpc.organization.getBySlugPublic.useQuery(
    {
      slug: router.query.slug as string,
    },
    {
      enabled: !router.isFallback && !!router.query.slug,
    },
  );

  if (getOrgBySlugPublicQuery.status === "pending") {
    return (
      <Center h="full">
        <Spinner />
      </Center>
    );
  }

  const org = getOrgBySlugPublicQuery.data;

  if (!org) {
    throw new NotFoundError("Organization not found");
  }

  return (
    <Flex direction="column" width="full" height="100vh" overflow="hidden">
      <NextSeo
        titleTemplate={`%s | ${org.name} Security Portal`}
        defaultTitle={`${org.name} Security Portal`}
      />
      <Box
        as="header"
        position="fixed"
        top={0}
        left={0}
        right={0}
        zIndex={1}
        bgColor="gray.900"
        borderBottomWidth={1}
        borderBottomColor="gray.700">
        <Container maxW="container.xl" py={4}>
          <Flex alignItems="center" justifyContent="space-between">
            <Link href={`/${router.query.slug}`}>
              <HStack spacing={4} alignItems="center" textDecoration="none" color="white">
                {org.avatarUrl && (
                  <Image src={org.avatarUrl} alt={`${org.name} logo`} boxSize={8} />
                )}
                <Text fontWeight="bold" fontSize={{ base: "sm", md: "lg" }} noOfLines={1}>
                  {org.name} - Security Portal
                </Text>
              </HStack>
            </Link>

            <HStack spacing={4} alignItems="center">
              {!isAdminDashboardPreview && (
                <Hide below="md">
                  {session.data && session.data.user.orgIds.includes(org.id) && (
                    <Button
                      size="sm"
                      onClick={() => {
                        void router.push(`/admin/${org.slug}/dashboard`);
                      }}
                      rightIcon={
                        <Icon as={LucideArrowRight} boxSize={4} color="gray.400" />
                      }>
                      Go to Admin Dashboard
                    </Button>
                  )}
                </Hide>
              )}

              <Link href={`/${router.query.slug}/report`}>
                <Show above="md">
                  <Button
                    size="sm"
                    textDecoration="none"
                    color="white"
                    variant="primary"
                    leftIcon={<Icon as={LucideShieldAlert} boxSize={4} />}>
                    Report a threat
                  </Button>
                </Show>
                <Hide above="md">
                  <Tooltip label="Report a threat">
                    <IconButton
                      size="sm"
                      textDecoration="none"
                      color="white"
                      aria-label="Report"
                      variant="primary"
                      icon={<Icon as={LucideShieldAlert} boxSize={4} />}
                    />
                  </Tooltip>
                </Hide>
              </Link>
            </HStack>
          </Flex>
        </Container>
      </Box>

      <Box as="main" pt={20} flex={1} minH={0} overflow="auto" bg="gray.900">
        <Container maxW="container.xl" h="full">
          {children}
        </Container>
      </Box>

      <DevToolbar />
    </Flex>
  );
}
